import type { AxiosResponse } from 'axios'
import { apiClient } from './client'
import type { components } from '@/types/api'
import type { DAGNode } from '@/types/visualization'
import { transformApiMessageToDAGNode, transformMessagesToDAGNodes } from '@/utils/apiTransform'

// Use auto-generated types from OpenAPI spec
type Message = components['schemas']['MessageSummary']
type MessagesResponse = components['schemas']['MessagesListResponse']
type MessageDetail = components['schemas']['MessageDetailResponse']
type NetworkOverview = components['schemas']['NetworkOverviewResponse']
type MessageSubmitRequest = components['schemas']['MessageSubmit']
type MessageSubmitResponse = components['schemas']['MessageSubmitResponse']

// WebSocket event types (these may need separate definition if not in OpenAPI)
type NewMessageEvent = {
  id: string
  timestamp: string
  depth: number
  proposer: string
}

type FinalityUpdateEvent = {
  message_id: string
  k_core_value?: number
  depth?: number
  homology_betti_b1?: number
}

type MetricsUpdateEvent = {
  total_messages?: number
  dag_depth?: number
  finalized_count?: number
  tps?: number
}

/**
 * Typed API client with proper response handling and transformations
 */
export class TypedApiClient {
  // Raw API methods that return exact backend response types
  // Now using auto-generated types from OpenAPI spec for perfect type alignment

  async getMessages(params?: {
    limit?: number
    offset?: number
    proposer?: string
    finalized?: boolean
    include_children?: boolean
  }): Promise<MessagesResponse> {
    const response: AxiosResponse<MessagesResponse> = await apiClient.get('/messages', { params })
    return response.data
  }

  async getMessage(id: string): Promise<MessageDetail> {
    const response: AxiosResponse<MessageDetail> = await apiClient.get(`/messages/${id}`)
    return response.data
  }

  async getAnalyticsOverview(): Promise<NetworkOverview> {
    const response: AxiosResponse<NetworkOverview> = await apiClient.get('/analytics/overview')
    return response.data
  }

  async submitMessage(data: MessageSubmitRequest): Promise<MessageSubmitResponse> {
    const response: AxiosResponse<MessageSubmitResponse> = await apiClient.post('/messages', data)
    return response.data
  }

  // Transformed methods that return frontend-ready data structures

  async getMessagesAsDAGNodes(params?: {
    limit?: number
    offset?: number
    proposer?: string
    finalized?: boolean
  }): Promise<DAGNode[]> {
    // Always include children for DAG visualization
    const messagesResponse = await this.getMessages({
      ...params,
      include_children: true
    })

    return transformMessagesToDAGNodes(messagesResponse.messages)
  }

  async getMessageAsDAGNode(id: string): Promise<DAGNode> {
    const message = await this.getMessage(id)

    // For single message, we need to fetch children separately
    const childrenResponse = await apiClient.get(`/messages/${id}/children`)
    const children = childrenResponse.data.children?.map((child: any) => child.id) || []

    return transformApiMessageToDAGNode(message, { children })
  }

  async getRecentMessagesForVisualization(limit = 100): Promise<DAGNode[]> {
    return this.getMessagesAsDAGNodes({ limit, include_children: true })
  }

  // Convenience methods for common use cases

  async getFinalizedMessages(limit = 50): Promise<DAGNode[]> {
    return this.getMessagesAsDAGNodes({
      limit,
      finalized: true,
      include_children: true
    })
  }

  async getMessagesByProposer(proposer: string, limit = 50): Promise<DAGNode[]> {
    return this.getMessagesAsDAGNodes({
      proposer,
      limit,
      include_children: true
    })
  }

  // Network health and metrics

  async getNetworkHealth(): Promise<{
    totalMessages: number
    dagDepth: number
    finalizedHeight: number
    tps: number
    finalizedCount: number
    healthStatus: 'healthy' | 'warning' | 'critical'
  }> {
    const overview = await this.getAnalyticsOverview()

    // Calculate health status based on metrics
    let healthStatus: 'healthy' | 'warning' | 'critical' = 'healthy'

    if (overview.tps < 0.1) healthStatus = 'critical'
    else if (overview.tps < 0.5) healthStatus = 'warning'

    if (overview.dag_depth < 5) healthStatus = 'warning'
    if (overview.dag_depth < 2) healthStatus = 'critical'

    return {
      totalMessages: overview.total_messages,
      dagDepth: overview.dag_depth,
      finalizedHeight: overview.finalized_height,
      tps: overview.tps,
      finalizedCount: overview.finalized_count,
      healthStatus
    }
  }

  // WebSocket event handlers (for type safety)

  handleNewMessageEvent(event: NewMessageEvent): void {
    // Update local caches, emit to stores, etc.
    console.debug('New message event:', event)
  }

  handleFinalityUpdateEvent(event: FinalityUpdateEvent): void {
    console.debug('Finality update event:', event)
  }

  handleMetricsUpdateEvent(event: MetricsUpdateEvent): void {
    console.debug('Metrics update event:', event)
  }
}

// Export singleton instance
export const typedApi = new TypedApiClient()

// Re-export the original api for backward compatibility
export { api } from './client'